<template>
  <div class="result mt-1 md:mt-0">
    <div v-if="!resultContent" class="loading">Loading...</div>
    <template v-else>
      <section v-if="resultContent.result_header_image" class="header-image">
        <img
          class="w-full h-full object-contain"
          v-lazy="imageAPIUrl + resultContent.result_header_image"
          alt="Dr Hauschka"
        />
      </section>
      <section class="grid grid-cols-1 container-small">
        <base-headline level="1" size="xlarge">{{ resultContent.headline }}</base-headline>
        <div class="mt-10 md:mt-20">
          <base-headline level="2" :size="Sizes.Large">{{ textSnippets.your_skin_type }}</base-headline>
          <base-text class="font-semibold mt-4">{{ resultContent.name }}</base-text>
          <base-text>{{ resultContent.description }}</base-text>
        </div>
        <feature name="save-result-to-shop-account">
          <div>
            <base-button
              class="mt-10 uppercase inline-flex"
              :disabled="!!!resultContent.url"
              :to="resultContent.url ?? ''"
              >{{ textSnippets.save_result }}</base-button
            >
          </div>
        </feature>
        <div class="mt-10">
          <base-headline level="2" :size="Sizes.Large">{{ textSnippets.your_care }}</base-headline>
          <base-text class="font-semibold mt-4">{{ resultContent.your_care_subheadline }}</base-text>
          <base-text class="block">{{ resultContent.care }}</base-text>
          <base-button class="mt-10 uppercase inline-flex" to="#dayProducts" v-smooth-scroll>{{
            textSnippets.show_recommendations
          }}</base-button>
        </div>
      </section>
      <section
        v-if="resultContent.layout === 'default'"
        class="mt-10 flex items-center justify-center space-x-14 container-small"
      >
        <a href="#daycare" v-smooth-scroll class="flex space-x-4.5 items-center group">
          <span>{{ textSnippets.day_care }}</span>
          <div class="relative">
            <sun class="opacity-0 group-hover:opacity-100 absolute transition delay-150 ease-in-out w-7 h-7" />
            <sun class="group-hover:opacity-0 transition delay-150 ease-in-out w-7 h-7" />
          </div>
        </a>
        <a href="#nightcare" v-smooth-scroll class="flex space-x-4.5 items-center group">
          <span>{{ textSnippets.night_care }}</span>
          <div class="relative">
            <moon class="opacity-0 group-hover:opacity-100 absolute transition delay-150 ease-in-out w-7 h-7" />
            <moon class="group-hover:opacity-0 transition delay-150 ease-in-out w-7 h-7" />
          </div>
        </a>
      </section>
      <section v-else class="mt-10 flex items-center justify-center space-x-4 container-small">
        <a href="#basicCare" v-smooth-scroll class="flex space-x-2 items-center group">
          <div class="relative">
            <ph-circle
              weight="fill"
              class="opacity-0 group-hover:opacity-100 absolute transition delay-150 ease-in-out"
            />
            <ph-circle />
          </div>
          <span>{{ textSnippets.basic_care }}</span></a
        >
        <a href="#acuteCare" v-smooth-scroll class="flex space-x-2 items-center group">
          <div class="relative">
            <ph-circle
              weight="fill"
              class="opacity-0 group-hover:opacity-100 absolute transition delay-150 ease-in-out"
            />
            <ph-circle />
          </div>
          <span>{{ textSnippets.acute_care }}</span></a
        >
      </section>
      <section v-if="resultContent.layout === 'default'" id="daycare" class="daycare">
        <module-banner
          class="mt-10 whitespace-pre-line container-small"
          :backgound-color="Colors.OffWhite"
          :orientation="Orientation.Right"
          :headline="`${textSnippets.day_care_headline || ''}
      ${textSnippets.day_care_subheadline || ''}`"
          :text="resultContent.care_block_1"
          :image="images.day_care_image"
        >
          <template #icon>
            <sun class="w-7 h-7" />
          </template>
          <template #cta>
            <base-text class="font-semibold">{{ textSnippets.daycare_products_headline }}</base-text>
          </template>
        </module-banner>
        <product-slider
          v-if="dayCareProducts && dayCareProducts.length"
          id="dayProducts"
          class="mt-10"
          :width-steps="true"
          :text-steps="textSteps"
          :pagination="false"
          :list-products="dayCareProducts"
        ></product-slider>
      </section>
      <section v-else id="basicCare" class="basicCare">
        <module-banner
          class="mt-10 whitespace-pre-line container-small"
          :backgound-color="Colors.OffWhite"
          :orientation="Orientation.Right"
          :headline="`${textSnippets.basic_care_headline || ''}
      ${textSnippets.basic_care_subheadline || ''}`"
          :text="resultContent.care_block_1"
          :image="images.med_care_image_1"
        >
          <template #cta>
            <base-text class="font-semibold">{{ textSnippets.daycare_products_med_headline }} </base-text>
          </template>
        </module-banner>
        <product-slider
          v-if="dayCareProducts && dayCareProducts.length"
          id="dayProducts"
          class="mt-10"
          :width-steps="true"
          :text-steps="textSteps"
          :pagination="false"
          :list-products="dayCareProducts"
        ></product-slider>
      </section>
      <section v-if="resultContent.layout === 'default'" id="nightcare" class="nightcare mt-10 md:mt-20">
        <module-banner
          class="mt-10 whitespace-pre-line container-small"
          :backgound-color="Colors.White"
          :orientation="Orientation.Right"
          :headline="`${textSnippets.night_care_headline || ''}
      ${textSnippets.night_care_subheadline || ''}`"
          :text="resultContent.care_block_2"
          :image="images.night_care_image"
        >
          <template #icon>
            <moon class="w-7 h-7" />
          </template>
          <template #cta>
            <base-text class="font-semibold"> {{ textSnippets.nightcare_products_headline }} </base-text>
          </template>
        </module-banner>
        <product-slider
          v-if="nightCareProducts && nightCareProducts.length"
          id="nightProducts"
          class="mt-10"
          :width-steps="true"
          :text-steps="textSteps"
          :pagination="false"
          :list-products="nightCareProducts"
        ></product-slider>
      </section>
      <section v-else id="acuteCare" class="acuteCare">
        <module-banner
          class="mt-10 whitespace-pre-line container-small"
          :backgound-color="Colors.Black"
          :orientation="Orientation.Right"
          :headline="`${textSnippets.acute_care_headline || ''}
      ${textSnippets.acute_care_subheadline || ''}`"
          :text="resultContent.care_block_2"
          :image="images.med_care_image_2"
        >
          <template #cta>
            <base-text class="font-semibold">{{ textSnippets.nightcare_products_med_headline }}</base-text>
          </template>
        </module-banner>
        <product-slider
          v-if="nightCareProducts && nightCareProducts.length"
          id="nightProducts"
          class="mt-10"
          :width-steps="true"
          :text-steps="textSteps"
          :pagination="false"
          :list-products="nightCareProducts"
        ></product-slider>
      </section>
      <module-divider class="mt-10 md:mt-20 container-small">{{
        textSnippets.additional_recommendations
      }}</module-divider>
      <section v-if="resultContent.additional_recommendations" class="recommendations mt-10 container-small">
        <base-text>
          {{ resultContent.additional_recommendations }}
        </base-text>
      </section>
      <section class="addition mt-20">
        <module-accordion
          v-if="beautyProducts && beautyProducts.length"
          :title="textSnippets.beauty_tip"
          :sub-title="textSnippets.beauty_tip_subheadline"
          :expand="expandableAccordionItem.beauty"
          @expand="expand('beauty')"
        >
          <div class="container-small">
            <base-text class="mt-4">{{ resultContent.beauty_tip }}</base-text>
          </div>
          <product-slider
            class="mt-10 mb-20"
            :width-steps="false"
            :pagination="false"
            :list-products="beautyProducts"
          ></product-slider>
        </module-accordion>
        <module-accordion
          v-if="intensiveProducts && intensiveProducts.length"
          class="mt-10"
          :title="textSnippets.intensive_care"
          :sub-title="textSnippets.intensive_care_subheadline"
          :expand="expandableAccordionItem.intense"
          @expand="expand('intense')"
        >
          <div class="container-small">
            <base-text class="mt-4">{{ resultContent.intensive_care }}</base-text>
          </div>
          <product-slider
            class="mt-10 mb-20"
            :width-steps="false"
            :textSteps="textSteps"
            :pagination="false"
            :list-products="intensiveProducts"
          ></product-slider>
        </module-accordion>
        <module-accordion
          v-if="bodyProducts && bodyProducts.length"
          class="mt-10"
          :title="textSnippets.body_care"
          :sub-title="textSnippets.body_care_subheadline"
          :expand="expandableAccordionItem.body"
          @expand="expand('body')"
        >
          <div class="container-small">
            <base-text class="mt-4">{{ resultContent.body_care }}</base-text>
          </div>
          <product-slider
            class="mt-10 mb-20"
            :width-steps="false"
            :textSteps="textSteps"
            :pagination="false"
            :list-products="bodyProducts"
          ></product-slider>
        </module-accordion>
      </section>

      <feature name="disable-cart">
        <module-cart v-if="cartOpen" :visible="cartOpen"></module-cart>
      </feature>
      <feature name="promo-popup">
        <module-promo-popup
          :description="promoPopupContent.description"
          :footer="promoPopupContent.footer"
          :headline="promoPopupContent.headline"
          :image="promoPopupContent.image"
        ></module-promo-popup>
      </feature>
    </template>
  </div>
</template>
<script lang="ts" setup>
import { FeatureToggleComponent as feature } from 'vue-feature-toggle'
import BaseHeadline from '@/components/base/BaseHeadline.vue'
import BaseText from '@/components/base/BaseText.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import ModuleDivider from '@/components/module/ModuleDivider.vue'
import ModuleBanner from '@/components/module/ModuleBanner.vue'
import ModuleAccordion from '@/components/module/ModuleAccordion.vue'
import { useMainStore } from '@/store/main'
import { computed, inject, reactive, ref } from 'vue'
import { Colors } from '@/types/colors'
import { Orientation } from '@/components/module/ModuleBanner.vue'
import { PhSun, PhMoon, PhCircle } from '@dnlsndr/vue-phosphor-icons'
import ProductSlider from '@/components/ProductSlider/ProductSlider.vue'
import { IProduct } from '@/components/ProductSlider/ProductSlider'
import ModuleCart from '@/components/Cart/ModuleCart.vue'
import ModulePromoPopup from '@/components/module/ModulePromoPopup.vue'
import { useRouter } from 'vue-router'
import { setFallbackWithException } from '@/utils/fallback'
import { Sizes } from '@/types/sizes'

import sun from '@/assets/icons/sun.svg'
import moon from '@/assets/icons/moon.svg'

/**
 * register store
 */
const store = useMainStore()

/**
 * register router
 */
const router = useRouter()

/**
 * get result content
 */
const resultContent = computed(() => store.result)

/**
 * get images
 */
const images = computed(() => store.images)

/**
 * @returns {boolean} cartOpen
 */
const cartOpen = computed(() => store.cartOpen)

/**
 * retdirect if no result exist
 */
if (Object.keys(resultContent.value).length === 0) {
  const restartQuiz: any = inject('restartQuiz')
  restartQuiz()
}

/**
 * inject config
 */
const config: any = inject('config')

/**
 * @returns {Promo} promoPopupContent
 */
const promoPopupContent = computed(() => store.promoPopup)

/**
 * @returns {Language} textSnippets
 */
// TODO: Load snippets on everypage if not existing
const textSnippets = computed(() => store.snippets)

const textSteps = computed(() =>
  resultContent.value.layout === 'default'
    ? [textSnippets.value.step_1, textSnippets.value.step_2, textSnippets.value.step_3]
    : undefined,
)

const imageAPIUrl = config.baseUrl + '/storage/'

/**
 * close cart if state is still open while entering the page HAUTEST-125
 */
store.closeCart()

/**
 * router guard for back navigation while cart is open
 * closes cart if open and prevent histroy back
 */
router.beforeEach((to, from) => {
  if (cartOpen.value) {
    store.closeCart()
    return false
  }
})

/**
 * Products
 * step_1 is daycare
 * step_2 nightcare
 *
 * accordion for extra tips:
 * body_care
 * intensive_care
 * beauty
 *
 */
/**
 * @returns {Products} dayCareProducts
 */
const dayCareProducts = computed(() => {
  const dayCareProductsArray = [
    {
      id: '1',
      title: textSnippets.value.step_clean,
      active: true,
      children: <IProduct[]>[],
    },
    {
      id: '2',
      active: false,
      title: textSnippets.value.step_strengthen,
      children: <IProduct[]>[],
    },
    {
      id: '3',
      active: false,
      title: textSnippets.value.step_care,
      children: <IProduct[]>[],
    },
  ]
  // loop step 1
  resultContent.value.products?.step_1_1?.forEach((item) => {
    dayCareProductsArray[0].children.push({
      title: item.name,
      productImage: imageAPIUrl + item.image,
      description: item.bullet_points,
      metaInfo: textSnippets.value.contents,
      metaNumber: item.contents,
      price: item.price?.value ?? setFallbackWithException(0, 'price'),
      currency: item.price?.currency.iso ?? setFallbackWithException('EUR', 'currency'),
      id: `${item.identifier}`,
      qty: 1,
      available: item.available,
    })
  })
  // loop step 2
  resultContent.value.products?.step_1_2?.forEach((item) => {
    dayCareProductsArray[1].children.push({
      title: item.name,
      productImage: imageAPIUrl + item.image,
      description: item.bullet_points,
      metaInfo: textSnippets.value.contents,
      metaNumber: item.contents,
      price: item.price?.value ?? setFallbackWithException(0, 'price'),
      currency: item.price?.currency.iso ?? setFallbackWithException('EUR', 'currency'),
      id: `${item.identifier}`,
      qty: 1,
      available: item.available,
    })
  })
  // loop step 3
  resultContent.value.products?.step_1_3?.forEach((item) => {
    dayCareProductsArray[2].children.push({
      title: item.name,
      productImage: imageAPIUrl + item.image,
      description: item.bullet_points,
      metaInfo: textSnippets.value.contents,
      metaNumber: item.contents,
      price: item.price?.value ?? setFallbackWithException(0, 'price'),
      currency: item.price?.currency.iso ?? setFallbackWithException('EUR', 'currency'),
      id: `${item.identifier}`,
      qty: 1,
      available: item.available,
    })
  })

  // filter empty arrays

  return dayCareProductsArray.filter((item) => item.children.length)
})

/**
 * @returns {Products} nightCareProducts
 */
const nightCareProducts = computed(() => {
  const nightCareProductsArray = [
    {
      id: '1',
      title: textSnippets.value.step_clean,
      active: true,
      children: <IProduct[]>[],
    },
    {
      id: '2',
      active: false,
      title: textSnippets.value.step_strengthen,
      children: <IProduct[]>[],
    },
    {
      id: '3',
      active: false,
      // change step name if layout is not default
      title: resultContent.value.layout === 'default' ? textSnippets.value.step_vitalize : textSnippets.value.step_care,
      children: <IProduct[]>[],
    },
  ]
  // loop step 1
  resultContent.value.products?.step_2_1?.forEach((item) => {
    nightCareProductsArray[0].children.push({
      title: item.name,
      productImage: imageAPIUrl + item.image,
      description: item.bullet_points,
      metaInfo: textSnippets.value.contents,
      metaNumber: item.contents,
      price: item.price?.value ?? setFallbackWithException(0, 'price'),
      currency: item.price?.currency.iso ?? setFallbackWithException('EUR', 'currency'),
      id: `${item.identifier}`,
      qty: 1,
      available: item.available,
    })
  })
  // loop step 2
  resultContent.value.products?.step_2_2?.forEach((item) => {
    nightCareProductsArray[1].children.push({
      title: item.name,
      productImage: imageAPIUrl + item.image,
      description: item.bullet_points,
      metaInfo: textSnippets.value.contents,
      metaNumber: item.contents,
      price: item.price?.value ?? setFallbackWithException(0, 'price'),
      currency: item.price?.currency.iso ?? setFallbackWithException('EUR', 'currency'),
      id: `${item.identifier}`,
      qty: 1,
      available: item.available,
    })
  })
  // loop step 3
  resultContent.value.products?.step_2_3?.forEach((item) => {
    nightCareProductsArray[2].children.push({
      title: item.name,
      productImage: imageAPIUrl + item.image,
      description: item.bullet_points,
      metaInfo: textSnippets.value.contents,
      metaNumber: item.contents,
      price: item.price?.value ?? setFallbackWithException(0, 'price'),
      currency: item.price?.currency.iso ?? setFallbackWithException('EUR', 'currency'),
      id: `${item.identifier}`,
      qty: 1,
      available: item.available,
    })
  })

  return nightCareProductsArray.filter((item) => item.children.length)
})

/**
 * @returns {Products} beautyProducts
 */
const beautyProducts = computed(() => {
  if (!Array.isArray(resultContent.value.products?.beauty_tip)) return null
  return resultContent.value.products?.beauty_tip?.map((item) => {
    return {
      title: item.name,
      productImage: imageAPIUrl + (item.deco_image || item.image),
      description: item.bullet_points,
      metaInfo: textSnippets.value.contents,
      metaNumber: item.contents,
      price: item.price?.value ?? setFallbackWithException(0, 'price'),
      currency: item.price?.currency.iso ?? setFallbackWithException('EUR', 'currency'),
      id: `${item.identifier}`,
      available: item.available,
    }
  })
})

/**
 * @returns {Products} intensiveProducts
 */
const intensiveProducts = computed(() => {
  if (!Array.isArray(resultContent.value.products?.intensive_care)) return null
  return resultContent.value.products?.intensive_care?.map((item) => {
    return {
      title: item.name,
      productImage: imageAPIUrl + (item.deco_image || item.image),
      description: item.bullet_points,
      metaInfo: textSnippets.value.contents,
      metaNumber: item.contents,
      price: item.price?.value ?? setFallbackWithException(0, 'price'),
      currency: item.price?.currency.iso ?? setFallbackWithException('EUR', 'currency'),
      id: `${item.identifier}`,
      available: item.available,
    }
  })
})

/**
 * @returns {Products} bodyProducts
 */
const bodyProducts = computed(() => {
  if (!Array.isArray(resultContent.value.products?.body_care)) return null
  return resultContent.value.products?.body_care?.map((item) => {
    return {
      title: item.name,
      productImage: imageAPIUrl + (item.deco_image || item.image),
      description: item.bullet_points,
      metaInfo: textSnippets.value.contents,
      metaNumber: item.contents,
      price: item.price?.value ?? setFallbackWithException(0, 'price'),
      currency: item.price?.currency.iso ?? setFallbackWithException('EUR', 'currency'),
      id: `${item.identifier}`,
      available: item.available,
    }
  })
})

const expandableAccordionItem = reactive({
  beauty: false,
  intense: false,
  body: false,
})

/**
 * @param {'beauty' | 'intense' | 'body'} item
 * @description expand function for accordion
 */
const expand = (item: 'beauty' | 'intense' | 'body') => {
  expandableAccordionItem[item] = !expandableAccordionItem[item]
}

// inject smooth scrolll
const smoothScroll: any = inject('smoothScroll')
const scrollToElem = (elem: string) => {
  smoothScroll({
    scrollTo: document.getElementById(elem),
  })
}
</script>

<style scoped>
.accordion :deep(.accordion-header) {
  @apply container-small;
}

.accordion :deep(hr) {
  @apply container-small;
}

.header-image + section {
  @apply mt-5;
}
@screen md {
  .header-image + section {
    @apply mt-10;
  }
}
</style>
